import { IonCol, IonGrid, IonRow, IonSplitPane } from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { ActiveRecordingTimer } from '@/components/ActiveRecordingTimer/ActiveRecordingTimer';
import { BottomBar, type BottomBarProps } from '@/components/BottomBar/BottomBar';
import { Button } from '@/components/Button';
import { CalibrationCompleted } from '@/components/CalibrationCompleted/CalibrationCompleted';
import { CalibrationCountdown } from '@/components/CalibrationCountdown/CalibrationCountdown';
import { RadioSelectButtonGroup } from '@/components/RadioSelectButtonGroup/RadioSelectButtonGroup';
import { RecordingCancelled } from '@/components/RecordingCancelled/RecordingCancelled';
import { RecordingProcessing } from '@/components/RecordingProcessing/RecordingProcessing';
import { SensorSidebar } from '@/components/SensorSidebar/SensorSidebar';
import { toast } from '@/components/Toaster/Toast';

import { isDevelopment, mergeClassNames } from '@/utils';

import { useActiveSessionStore } from '@/hooks/useActiveSessionStore';
import { useSensors } from '@/hooks/useSensors';
import { useSessionScreenStore } from '@/hooks/useSessionScreenStore';

import { useSensorsContext } from '@/contexts/SensorsContext';

import { Recording } from '@/store/ActiveSessionStore';

import styles from './RecordingScreen.module.css';

import { MovementType } from '@/types/MovementTypes';

interface RecordingScreenProps {
  isHubFlow: boolean;
}

type Step = BottomBarProps & {
  showBottomBar: boolean;
};

export const RecordingScreen = ({ isHubFlow }: RecordingScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentMovementType, setCurrentMovementType] = useState<MovementType | undefined>();
  const [activeRecording, setActiveRecording] = useState<Recording>();
  const [lostSensorConnection, setLostSensorConnection] = useState(false);
  const [recordingUploadingProgress, setRecordingProgress] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const { hubStatus, state } = useSensorsContext();

  const { addRecording, updateRecording, activeTestId } = useActiveSessionStore();
  const { startMeasurement, stopMeasurement, disconnectAll } = useSensors(isHubFlow);
  const { goToNextScreen } = useSessionScreenStore();
  const history = useHistory();

  const [hadFailure, setHadFailure] = useState(false);

  useEffect(() => {
    const hasFailure = state.failedSensors.length > 0;
    if (hasFailure && !hadFailure) {
      setHadFailure(true);
      setLostSensorConnection(true);

      const failedSensorNames = state.failedSensors.map((sensor) => {
        return state.sensorsInDatabase.find((s) => s.id === sensor)?.title;
      });

      failedSensorNames.forEach((sensorName) => {
        toast({
          variant: 'error',
          title: 'Connection to sensor lost',
          description: `${sensorName ? `"${sensorName}"` : 'Sensor'} lost connection`,
        });
      });

      setCurrentStep(3);
    }

    if (!hasFailure && hadFailure && state.allConnected) {
      setHadFailure(false);
      setLostSensorConnection(false);

      toast({
        variant: 'success',
        title: 'Connection to sensor(s) recovered',
        description: 'Sensor(s) regained connection',
      });
    }
  }, [state.failedSensors, state.allConnected, hadFailure, state.sensorsInDatabase]);

  const handleFinishCountdown = () => {
    if (!activeRecording) {
      throw new Error('No active recording');
    }

    updateRecording({
      ...activeRecording,
      state: 'recording',
    });

    setStartTime(new Date().getTime());
    setCurrentStep(2);
  };

  const handleStartRecording = () => {
    if (!currentMovementType || !activeTestId) {
      throw new Error('No movement type selected');
    }

    setActiveRecording(addRecording(currentMovementType, activeTestId));

    // Start the measurement on the sensor
    startMeasurement(currentMovementType);

    setCurrentMovementType(undefined);
    setCurrentStep(1);
  };

  const handleEndRecording = () => {
    if (!activeRecording) {
      throw new Error('No active recording');
    }

    updateRecording({
      ...activeRecording,
      state: undefined,
    });

    // Tell the sensors to stop measuring
    // In the hub flow this will set the annotation count to 0
    stopMeasurement();

    setActiveRecording(undefined);
    setCurrentStep(5);
  };

  const handleFinish = () => {
    // TODO: process recording(s) and save to database
    if (import.meta.env.MODE !== 'production') {
      console.log('Finish recording');
    }

    setRecordingProgress(0);

    // Disconnect all the sensors
    disconnectAll();

    // On the hub flow we have some extra screens to go to before we're finished
    if (isHubFlow) {
      goToNextScreen();
      return;
    }

    history.goBack();
  };

  const handleNewRecording = () => {
    // * possible to already process the recording here
    setCurrentStep(0);
  };

  const handleProcessRecording = () => {
    // Hub flow can continue without uploading first
    if (isHubFlow) {
      return handleEndRecording();
    }

    setCurrentStep(4);

    // TODO: process recording and update progress
    setRecordingProgress(0.2);

    setTimeout(() => {
      handleEndRecording();
    }, 3000);
  };

  const isButtonDisabled = hubStatus === 'disconnected' && currentStep === 2;

  const steps = useMemo<Step[]>(() => {
    const steps: Step[] = [
      {
        title: 'Start recording',
        description: 'Ready to start recording! Select your exercise type and tap start recording',
        nextButton: {
          label: 'Continue',
          onClick: handleStartRecording,
          isDisabled: currentMovementType === undefined,
        },
        showBottomBar: true,
      },
      {
        title: 'Calibrating',
        description: 'Have the client stand still until the recording starts',
        nextButton: {
          label: 'End recording',
          trailingIcon: undefined,
          onClick: handleEndRecording,
          isDisabled: true,
        },
        showBottomBar: true,
      },
      {
        title: 'Recording',
        description:
          activeRecording?.movementType === MovementType.walk
            ? 'Walk in a straight line for about 15 - 20 meters, turn around and walk back'
            : activeRecording?.movementType === MovementType.run
              ? 'Run in a straight line for about 30 - 50 meters, stop, turn around and run back'
              : activeRecording?.movementType === MovementType.doubleLegSquat
                ? 'Perform 5 - 10 double leg squats in a timing of 1 sec down, 1 sec hold and 1 sec up, 1 sec pause'
                : activeRecording?.movementType === MovementType.singleLegSquat
                  ? 'Perform 5 - 10 single leg squats on each leg pause for 1 sec and then switch legs'
                  : 'Recording in progress',
        nextButton: {
          label: 'End recording',
          trailingIcon: undefined,
          onClick: handleProcessRecording,
          isDisabled: isButtonDisabled,
        },
        showBottomBar: true,
      },
      {
        title: 'Recording cancelled',
        description: '',
        showBottomBar: false,
      },
      {
        title: 'Uploading recording',
        description: '',
        showBottomBar: false,
      },
      {
        title: 'Finished',
        description: '',
        showBottomBar: false,
      },
    ];

    return steps;
  }, [currentStep, currentMovementType, isButtonDisabled, activeRecording]);

  return (
    <IonSplitPane className={styles.splitPane} contentId='recording' when='xs'>
      <SensorSidebar disabled={currentStep === 4} isHubFlow={isHubFlow} />

      <div className={mergeClassNames('ion-page', 'ion-no-padding', styles.content)} id='recording'>
        <IonGrid className={mergeClassNames('ion-no-padding', styles.grid)} fixed>
          <IonRow className={mergeClassNames(styles.topArea, !steps[currentStep].showBottomBar && styles.noBottomBar)}>
            {currentStep === 0 && (
              <IonCol className={styles.content} size='12'>
                <RadioSelectButtonGroup currentMovementType={currentMovementType} onChange={setCurrentMovementType} />
              </IonCol>
            )}

            {currentStep === 1 && (
              <IonCol className={styles.content} size='12'>
                <CalibrationCountdown isPlaying={true} onFinish={handleFinishCountdown} />
              </IonCol>
            )}

            {currentStep === 2 && startTime && (
              <IonCol className={styles.content} size='12'>
                <ActiveRecordingTimer startTime={startTime} />
              </IonCol>
            )}

            {currentStep === 3 && (
              <IonCol className={styles.content} size='12'>
                <RecordingCancelled
                  endSessionButton={{
                    onClick: handleFinish,
                  }}
                  isHubFlow={isHubFlow}
                  newRecordingButton={{
                    disabled: lostSensorConnection,
                    onClick: handleNewRecording,
                  }}
                />
              </IonCol>
            )}

            {currentStep === 4 && (
              <IonCol className={styles.content} size='12'>
                <RecordingProcessing progress={recordingUploadingProgress} />
              </IonCol>
            )}

            {currentStep === 5 && (
              <IonCol className={styles.content} size='12'>
                <CalibrationCompleted description='Continue or finish session' title='Recording completed' />
                <div className={styles.buttons}>
                  <Button isFullWidth onClick={handleFinish} size='small' variant='secondary'>
                    End session
                  </Button>
                  <Button isFullWidth onClick={handleNewRecording} size='small' variant='primary'>
                    New recording
                  </Button>
                </div>
              </IonCol>
            )}
          </IonRow>

          {steps[currentStep].showBottomBar && (
            <IonRow className={styles.bottomArea}>
              <IonCol size='12'>
                <BottomBar {...steps[currentStep]} />
              </IonCol>
            </IonRow>
          )}
        </IonGrid>
      </div>
    </IonSplitPane>
  );
};
